import React, { useState, useMemo } from "react";
import { Search, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreVertical } from "lucide-react";
import { Button } from "../../@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../@/components/ui/dropdown-menu";
import { Badge } from "../../@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../@/components/ui/select";
import { Input } from "../../@/components/ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from "../../@/components/ui/table";
import Pagination from "./pagination";
import DefaultTable from "./defaultTable";
import { Spinner } from "./spinner";


export const AdminDataTable = ({
  columns = [],
  data = [],
  onEdit,
  onDelete,
  onAssignUsers,
  onAssignRole,
  enableBulkEditRoles,
  enableBulkEditAction = true,
  onBulkAction,
  bulkActions: extraBulkActions = [],
  loading = false,
  headerClassName,
  paginationMode = "client",
  currentPage: controlledCurrentPage,
  rowsPerPage: controlledRowsPerPage,
  totalPages: controlledTotalPages,
  totalRows: controlledTotalRows,
  rowsPerPageOptions = [10, 25, 50],
  onPageChange: onPageChangeControlled,
  onRowsPerPageChange: onRowsPerPageChangeControlled,
  rowLabel,
  showSelectedCount,
  selectionResetKey,
}) => {
  const isServerPagination = paginationMode === "server";

  const [uncontrolledCurrentPage, setUncontrolledCurrentPage] = useState(1);
  const [uncontrolledRowsPerPage, setUncontrolledRowsPerPage] = useState(10);

  const currentPage = isServerPagination
    ? (controlledCurrentPage ?? 1)
    : uncontrolledCurrentPage;
  const rowsPerPage = isServerPagination
    ? (controlledRowsPerPage ?? 10)
    : uncontrolledRowsPerPage;

  const setCurrentPage = isServerPagination
    ? (onPageChangeControlled ?? (() => {}))
    : setUncontrolledCurrentPage;
  const setRowsPerPage = isServerPagination
    ? (onRowsPerPageChangeControlled ?? (() => {}))
    : setUncontrolledRowsPerPage;
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  React.useEffect(() => {
    if (selectionResetKey === undefined) return;
    setSelectedIds(new Set());
  }, [selectionResetKey]);

  const getRowId = (row) => {
    if (row?.email && row?.pathway) return `${row.email}-${row.pathway}`;
    return row?.id ?? row?._id ?? JSON.stringify(row);
  };

  const totalPages = isServerPagination
    ? Math.max(1, Number(controlledTotalPages ?? 1))
    : Math.max(1, Math.ceil((data?.length || 0) / rowsPerPage));

  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = isServerPagination
    ? (data || [])
    : (data || []).slice(startIndex, startIndex + rowsPerPage);

  const paginatedDataWithIds = useMemo(
    () =>
      paginatedData.map((row) => ({
        ...row,
        id: getRowId(row),
      })),
    [paginatedData]
  );

  const headerClass = headerClassName ?? "text-sm font-heading text-(--neutral-700) dark:text-(--neutral-900)";

  const derivedColumns = useMemo(() => {
    const checkboxCol = { key: "checkbox" };

    const hasCallerActionCol = Array.isArray(columns)
      ? columns.some((c) => String(c?.key || "") === "action")
      : false;

    const mappedCols =
      columns
        // Avoid duplicating special columns that DefaultTable/AdminDataTable manage.
        ?.filter((col) => {
          const key = String(col?.key || "");
          return key !== "checkbox";
        })
        .map((col) => ({
        key: col.key,
        header: <span className={headerClass}>{col.label}</span>,
        render: col.render
          ? (row) => col.render(row[col.key], row)
          : (row) => row[col.key] ?? "—",
      })) ?? [];

    const actionCol = {
      key: "action",
      header: <span className={headerClass}></span>,
      render: (row) => (
        <div className="flex items-center gap-2">
          {(onEdit || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-(--neutral-600) hover:bg-(--neutral-100)">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className=""
              >
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(row)} className="font-body">
                    Edit
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={() => onDelete(row)}
                    className="text-(--color-destructive) font-body"
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
    };

    // Only add the internal action column when we actually have edit/delete actions
    // AND the caller hasn't provided their own `action` column.
    const shouldAddInternalAction = Boolean((onEdit || onDelete) && !hasCallerActionCol);
    return shouldAddInternalAction
      ? [checkboxCol, ...mappedCols, actionCol]
      : [checkboxCol, ...mappedCols];
  }, [columns, onEdit, onDelete]);


   const toggleSelectRow = (id) =>
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });


  const toggleExpand = (id) => {
    setExpandedRowId(prev => (prev === id ? null : id));
  };

  const toggleSelectAll = (checked, pageRowKeys = []) =>
    setSelectedIds((prev) => {
      const copy = new Set(prev);

      if (checked) {
        (pageRowKeys || []).forEach((k) => copy.add(k));
        return copy;
      }

      (pageRowKeys || []).forEach((k) => copy.delete(k));
      return copy;
    });

  return (
    <div className="space-y-4">
      <DefaultTable
        columns={derivedColumns}
        data={paginatedDataWithIds}
        selectedItems={[...selectedIds]}
        onSelectItem={toggleSelectRow}
        onSelectAll={toggleSelectAll}
        bulkActions={Array.from(
          new Set([
            ...(onAssignRole ? ["Assign Role"] : []),
            ...(enableBulkEditRoles ? ["Edit Roles"] : []),
            ...(onEdit && enableBulkEditAction ? ["Edit"] : []),
            ...(onDelete ? ["Delete"] : []),
            ...((Array.isArray(extraBulkActions) ? extraBulkActions : []).filter(Boolean)),
          ])
        )}
        onBulkAction={onBulkAction}
        emptyMessage={loading ? <Spinner /> : "No items found."}
      />

      {/* footer / pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={(option) => {
          setRowsPerPage(option);
          setCurrentPage(1);
        }}
        onPageChange={setCurrentPage}
        totalRows={isServerPagination ? (controlledTotalRows ?? 0) : (data?.length || 0)}
        selectedCount={selectedIds.size}
        rowLabel={rowLabel}
        showSelectedCount={showSelectedCount}
      />

    </div>

  );
};

export default AdminDataTable;
